import './SelectionTable.scss';

import classNames from 'classnames';
import { getDirectParent } from '@lib/popups';
import { SelectionInfo } from './SelectionInfo';
import { SonarTable } from '@ui/SonarTable';
import { SortByFilterQueryParams } from '@periscopes/general/filters';
import { TableProps } from 'antd/es/table';

import { Col, Row } from 'antd';
import { ColumnsType, TableLocale, TablePaginationConfig } from 'antd/es/table/interface';
import { Else, If, Then, When } from 'react-if';
import React, { useCallback, useMemo } from 'react';
import { useCurrentUser, UseSelectionTableResult, UseSelectionTableWithInversionResult } from '@hooks';

function getCellFromElement(element: HTMLElement): HTMLElement | null {
	if (element.tagName === 'TD') {
		return element;
	}

	if (element.parentElement) {
		return getCellFromElement(element.parentElement);
	}

	return null;
}

export type SelectionTableQueryParams = SortByFilterQueryParams;

type TableRecord = Record<string, unknown>;
type UseSelectionTableWithInversionExtraProps = {
	selectAllEnabled?: boolean;
};

export type SelectionTableProps<TRecord extends TableRecord> = TableProps<TRecord> & {
	actionBar?: React.ReactNode;
	alwaysShowCount?: boolean;
	hideSelectAll?: boolean;
	isActionBarAbove?: boolean;
	emptyLabel?: React.ReactNode | (() => React.ReactNode);
	hideSelection?: boolean;
	forceShowSelection?: boolean;
	id?: string;
	onEditSelection?: (button: HTMLElement) => void;
	onRowClick?: (record: TRecord, index: number) => void;
	pagination?: boolean | TablePaginationConfig;
	renderRow?: (item: TRecord) => JSX.Element;
	selectionData: Omit<UseSelectionTableResult<TRecord> & UseSelectionTableWithInversionExtraProps, 'refreshIds'> |
        UseSelectionTableWithInversionResult<TRecord, string | number>;
	selectionType?: string;
};

function SelectionTableInner<TRecord extends TableRecord>({
	actionBar,
	alwaysShowCount = false,
	isActionBarAbove = true,
	bordered,
	className,
	columns,
	dataSource,
	emptyLabel,
	hideSelection,
	forceShowSelection,
	id,
	pagination = false,
	onEditSelection,
	onRowClick,
	// eslint-disable-next-line react/jsx-no-useless-fragment
	renderRow = () => <></>,
	scroll,
	selectionData,
	selectionType,
	...rest
}: SelectionTableProps<TRecord>, ref: React.ForwardedRef<HTMLDivElement>) {
	const {
		handleTablePageSelectAll,
		handleTableRowSelect,
		pageSelectedKeys,
		resetSelection,
		selectAll,
		selectedCount,
		selectAllEnabled,
		selectedKeys,
		totalCount
	} = selectionData;

	const { isReadOnly } = useCurrentUser();

	const isMultiColumn = Number(columns?.length) > 1;

	const tableLocale = useMemo(() => {
		const result: TableLocale | undefined = emptyLabel ? { emptyText: emptyLabel } : undefined;

		return result;
	}, [ emptyLabel ]);

	const handleRowConfig = useCallback(
		(record: TRecord, index: number) => ({
			onClick: (e: React.MouseEvent<HTMLTableCellElement>) => {
				const cell = getCellFromElement(e.target as HTMLElement);
				const isSelectionCell = cell?.classList.contains('ant-table-selection-column');

				if (!isSelectionCell && onRowClick) {
					onRowClick(record, index);
				}
			}
		}),
		[ onRowClick ]
	);

	const selectionInfoActive = selectAllEnabled || Boolean(selectedKeys.length);

	const getSelectionInfo = useCallback(
		() =>
			<If condition={ selectionInfoActive || isMultiColumn }>
				<Then>
					<Row>
						<Col span={ isActionBarAbove ? 24 : 12 }>
							<SelectionInfo
								active={ selectionInfoActive }
								afterClose={ resetSelection }
								alwaysShowCount={ alwaysShowCount }
								bordered={ isMultiColumn && !onEditSelection }
								id={ id }
								onEdit={ onEditSelection }
								onSelectAll={ selectAll }
								selectedCount={ selectedCount }
								selectionType={ selectionType }
								totalCount={ totalCount ?? 0 } />
						</Col>
						<Col span={ isActionBarAbove ? 0 : 12 }>{ !isActionBarAbove && actionBar }</Col>
					</Row>
				</Then>
				<Else>{ selectionType }</Else>
			</If>
		,
		[
			id,
			isMultiColumn,
			onEditSelection,
			resetSelection,
			selectAll,
			selectedCount,
			selectionInfoActive,
			selectionType,
			totalCount,
			actionBar,
			isActionBarAbove,
			alwaysShowCount
		]
	);

	const renderColumns = useMemo(
		() =>
			columns || [
				{
					render: (value, item) => renderRow(item),
					title: getSelectionInfo()
				}
			] as ColumnsType<TRecord>,
		[ columns, getSelectionInfo, renderRow ]
	);

	const title = isMultiColumn && !hideSelection ? getSelectionInfo : undefined;
	const componentClassNames = classNames({ [ String(className) ]: Boolean(className) });
	const forceHide = typeof forceShowSelection === 'boolean' && !forceShowSelection;
	const selectionEnabled = forceShowSelection || (!isReadOnly && !forceHide);

	const tableClassNames = classNames(className, {
		bordered,
		'multi-column': isMultiColumn,
		'selection-disabled': !selectionEnabled
	});

	const rowSelection = {
		onSelect: handleTableRowSelect,
		onSelectAll: handleTablePageSelectAll,
		selectedRowKeys: pageSelectedKeys
	};

	return (
		<div
			className={ classNames('selection-table', componentClassNames) }
			ref={ ref }>
			<When condition={ Boolean(actionBar) && isActionBarAbove }>
				<Row
					align='bottom'
					justify='space-between'>
					{ actionBar }
				</Row>
			</When>
			<SonarTable
				className={ tableClassNames }
				columns={ renderColumns }
				dataSource={ dataSource }
				getPopupContainer={ getDirectParent }
				locale={ tableLocale }
				onRow={ handleRowConfig }
				pagination={ pagination }
				rowClassName={ onRowClick ? 'clickable' : '' }
				rowSelection={ selectionEnabled ? rowSelection : undefined }
				scroll={ {
					y: 'content',
					...scroll
				} }
				title={ title }
				{ ...rest }
				bordered={ false } />
		</div>
	);
}

export const SelectionTable = React.forwardRef(SelectionTableInner);
